import { Link } from 'react-router-dom';
import { useState, FormEvent } from 'react';

interface CTASectionProps {
  formUrl: string;
}

export default function CTASection({ formUrl }: CTASectionProps) {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = form.querySelector<HTMLInputElement>('input[name="website_alt"]');
    if (honeypot && honeypot.value.trim() !== '') {
      setStatus('success');
      setEmail('');
      return;
    }
    setStatus('loading');
    try {
      const formData = new FormData(form);
      const params = new URLSearchParams();
      formData.forEach((value, key) => { params.append(key, value.toString()); });
      const res = await fetch(formUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      const responseText = await res.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      if (res.ok && parsed?.code === 'OK') {
        setStatus('success');
        setEmail('');
      } else {
        const msg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        setStatus('error');
        setErrorMsg(msg);
      }
    } catch {
      setStatus('error');
      setErrorMsg('网络异常，请稍后重试');
    }
  };

  return (
    <section className="relative py-16 md:py-24 overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Warm%20abstract%20gradient%20background%20with%20soft%20amber%20and%20cream%20tones%20flowing%20organic%20shapes%20modern%20collaborative%20workspace%20atmosphere%20minimalist%20elegant%20editorial%20style%20with%20gentle%20lighting%20and%20harmonious%20warm%20neutral%20palette&width=1800&height=700&seq=cta-bg-yiqicao&orientation=landscape"
          alt=""
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-primary-800/90 via-primary-700/80 to-primary-900/90"></div>
      </div>

      <div className="relative z-10 max-w-3xl mx-auto px-4 md:px-6 text-center">
        <h2 className="text-2xl md:text-4xl font-heading font-bold text-background-50 mb-4">
          准备好开始高效协作了吗？
        </h2>
        <p className="text-base text-background-50/80 mb-10 max-w-xl mx-auto leading-relaxed">
          免费注册一起草，立即体验多人协同文稿创作。无需安装，打开浏览器即可开始。
        </p>

        {status === 'success' ? (
          <div className="flex flex-col items-center gap-3">
            <div className="w-14 h-14 rounded-full bg-accent-500 flex items-center justify-center">
              <i className="ri-check-line text-2xl text-background-50"></i>
            </div>
            <p className="text-background-50 font-medium">订阅成功！我们会将最新动态发送到您的邮箱。</p>
          </div>
        ) : (
          <form
            onSubmit={handleSubmit}
            data-readdy-form
            className="flex flex-col sm:flex-row items-center gap-3 max-w-md mx-auto"
          >
            <input
              type="email"
              name="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="输入你的工作邮箱"
              required
              className="w-full sm:flex-1 px-5 py-3.5 bg-background-50/15 backdrop-blur-sm border border-background-50/30 rounded-lg text-background-50 placeholder:text-background-50/50 text-sm focus:outline-none focus:border-background-50/60 transition-colors"
            />
            <input
              type="text"
              name="website_alt"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
              className="anti-spam-field"
            />
            <button
              type="submit"
              disabled={status === 'loading'}
              className="w-full sm:w-auto px-8 py-3.5 bg-background-50 hover:bg-background-100 text-primary-700 font-semibold rounded-lg transition-all duration-200 text-sm whitespace-nowrap disabled:opacity-60"
            >
              {status === 'loading' ? '提交中...' : '免费开始使用'}
            </button>
          </form>
        )}
        {status === 'error' && (
          <p className="mt-3 text-sm text-background-50/80">{errorMsg}</p>
        )}

        <p className="mt-5 text-xs text-background-50/50">
          无需信用卡 · 免费版支持5人团队
        </p>
      </div>
    </section>
  );
}